// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux — DÉPORTS (conflit d'intérêts) : une personne se déporte pour un proche ; elle n'accomplit
// plus aucun acte de décision ni acte financier sur les dossiers de ce salarié dans l'organisation (refus
// DEPORT_DECLARE du serveur). Déclaration : la personne elle-même (mandataire de l'organisation) ou l'administrateur
// des mandats pour une autre personne. Jamais supprimé : seule la fin se pose.
//   • fenêtre de déclaration (depuis un dossier : salarié déjà connu ; depuis « Instances et mandats » : recherche) ;
//   • carte « Déports » : liste et historique (route proposée DM-WEB-005, absente → « Fonction bientôt disponible »,
//     les déports déclarés pendant la session restent affichés), fin d'un déport.
// Routes : POST /api/dispositifs/gouvernance/deports, POST /deports/:id/terminer, GET /personnes?q= (administrateur).
// Composants définis hors de tout parent (react-component-rules, règle 1).
// ═══════════════════════════════════════════════════════════════════════
import { useEffect, useState } from 'react';
import { Search, ShieldOff, UserX } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Badge, Bouton, BandeauInfo } from '../../design/composants';
import { toArray, aujourdhuiDakar, erreurDe } from './api';
import { Champ, PaginationDs } from './commun';
import { dateLongue } from './apiSocial';
import { useLectureSociale, FenetreActe, TitreSection, ChampMotif, ChoixRadio } from './communSocial';
import { ChoixPersonne } from './FenetresMandat';
import { useMiseEnPlace, LIENS_DEPORT } from './apiMiseEnPlace';

const nomDe = (p) => (p ? [p.prenom, (p.nom || '').toUpperCase()].filter(Boolean).join(' ') || p.nom || p.salarie || '' : '');

/** Recherche d'une personne enregistrée (administrateur des mandats : GET /gouvernance/personnes). */
function ChoixPersonneGouv({ libelleChamp, salariesSeulement = false, choisie, onChoisir }) {
  const api = useMiseEnPlace();
  const [q, setQ] = useState('');
  const [resultats, setResultats] = useState(null);
  const [erreur, setErreur] = useState(null);
  useEffect(() => {
    const terme = q.trim();
    if (terme.length < 2) { setResultats(null); return undefined; }
    let vivant = true;
    const t = setTimeout(() => {
      api.chercherPersonnesGouv(terme)
        .then((r) => { if (vivant) { setResultats(toArray(r).filter((p) => !salariesSeulement || p.nature === 'SALARIE')); setErreur(null); } })
        .catch((e) => { if (vivant) { setErreur(erreurDe(e, 'Recherche impossible.')); setResultats(null); } });
    }, 300);
    return () => { vivant = false; clearTimeout(t); };
  }, [api, q, salariesSeulement]);
  if (choisie) {
    return (
      <Champ libelle={libelleChamp} requis>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 14 }}>
          <strong style={{ fontWeight: 500 }}>{nomDe(choisie)}</strong><span style={{ color: 'var(--sg-texte-3)' }}>{choisie.matricule || ''}</span>
          <Bouton petit variante="lien" onClick={() => onChoisir(null)}>Changer</Bouton>
        </div>
      </Champ>
    );
  }
  return (
    <div style={{ marginBottom: 12 }}>
      <Champ libelle={libelleChamp} requis aide="Nom, prénom ou matricule (2 caractères au moins).">
        <div className="rh-recherche"><Search size={16} aria-hidden="true" />
          <input type="search" className="rh-champ-saisie" aria-label={libelleChamp} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher" />
        </div>
      </Champ>
      {erreur && <p style={{ margin: '-6px 0 8px', fontSize: 13, color: 'var(--sg-danger)' }}>{erreur.message}</p>}
      {resultats && !resultats.length && <p className="rh-form-aide" style={{ margin: '-6px 0 8px' }}>Aucune personne trouvée.</p>}
      {resultats && resultats.length > 0 && (
        <ul role="listbox" aria-label="Personnes trouvées" style={{ listStyle: 'none', margin: '-4px 0 0', padding: 0, border: '1px solid var(--sg-bord)', borderRadius: 6, maxHeight: 200, overflowY: 'auto' }}>
          {resultats.map((p) => (
            <li key={p.id}>
              <button type="button" role="option" aria-selected="false" className="rh-menu-item" style={{ width: '100%' }} disabled={p.eligible === false} onClick={() => onChoisir(p)}>
                <span>{nomDe(p)}</span><span style={{ color: 'var(--sg-texte-3)', marginLeft: 6 }}>{p.matricule || (p.nature === 'EXTERNE' ? 'membre externe' : '')}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Déclarer un déport. `salarie` ({ id, nom, matricule }) : salarié du dossier, déjà connu ; sinon recherché.
 * `administre` : l'administrateur des mandats peut déclarer pour une autre personne.
 */
export function FenetreDeclarerDeport({ organisationId, salarie = null, administre = false, onFermer, onFait }) {
  const api = useMiseEnPlace();
  const { employe } = useAuth();
  const [pour, setPour] = useState('MOI');
  const [personne, setPersonne] = useState(null);
  const [concerne, setConcerne] = useState(salarie);
  const [lien, setLien] = useState('');
  const [autre, setAutre] = useState('');
  const [motif, setMotif] = useState('');
  const [debut, setDebut] = useState(aujourdhuiDakar());
  const [fin, setFin] = useState('');
  const lienTexte = (lien === 'AUTRE' ? autre : LIENS_DEPORT[lien] || '').trim();
  const qui = pour === 'MOI' ? employe : personne;
  const surSoi = !!(qui && concerne && String(qui.id) === String(concerne.id));
  const pret = !!(qui && concerne && !surSoi && lienTexte.length >= 2 && lienTexte.length <= 40 && debut && (!fin || fin >= debut));
  return (
    <FenetreActe titre="Déclarer un conflit d'intérêts (déport)" Icone={ShieldOff} libelleBouton="Déclarer le déport" pret={pret} onFermer={onFermer} large
      aide="La personne déportée n'accomplira plus aucun acte de décision ni acte financier sur les dossiers de ce salarié dans l'organisation ; une autre personne habilitée les traitera. Le déport ne se supprime pas : seule sa fin se pose."
      onConfirmer={async () => {
        const r = await api.declarerDeport({
          organisation_id: organisationId, employe_id: concerne.id, lien: lienTexte, date_debut: debut,
          ...(pour === 'AUTRE' ? { personne_id: personne.id } : {}), ...(motif.trim() ? { motif: motif.trim() } : {}), ...(fin ? { date_fin: fin } : {}),
        });
        onFait(`Déport déclaré pour ${nomDe(concerne)} à compter du ${dateLongue(debut)}.`, { ...r, personne: nomDe(qui), salarie: nomDe(concerne), lien: lienTexte });
      }}>
      {administre && <ChoixRadio nom="pour" options={{ MOI: 'Je me déporte', AUTRE: 'Déclarer pour un autre membre (administrateur des mandats)' }} valeur={pour} onChange={setPour} />}
      {pour === 'AUTRE' && <ChoixPersonneGouv libelleChamp="Personne qui se déporte" choisie={personne} onChoisir={setPersonne} />}
      {salarie ? (
        <Champ libelle="Salarié concerné"><div style={{ fontSize: 14 }}><strong style={{ fontWeight: 500 }}>{nomDe(salarie)}</strong> <span style={{ color: 'var(--sg-texte-3)' }}>{salarie.matricule || ''}</span></div></Champ>
      ) : administre ? (
        <ChoixPersonneGouv libelleChamp="Salarié concerné" salariesSeulement choisie={concerne} onChoisir={setConcerne} />
      ) : (
        <ChoixPersonne choisie={concerne} onChoisir={setConcerne} />
      )}
      {surSoi && <BandeauInfo ton="alerte">Un déport concerne un proche, jamais soi-même : on n'agit de toute façon jamais sur son propre dossier.</BandeauInfo>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
        <Champ libelle="Lien avec le salarié" requis>
          <select className="rh-champ-saisie" value={lien} onChange={(e) => setLien(e.target.value)}>
            <option value="">Choisir…</option>
            {Object.entries(LIENS_DEPORT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Champ>
        {lien === 'AUTRE' && <Champ libelle="Préciser le lien" requis aide="2 à 40 caractères."><input className="rh-champ-saisie" maxLength={40} value={autre} onChange={(e) => setAutre(e.target.value)} /></Champ>}
        <Champ libelle="À compter du" requis><input type="date" className="rh-champ-saisie" value={debut} onChange={(e) => setDebut(e.target.value)} /></Champ>
        <Champ libelle="Jusqu'au" aide="Facultatif : sans date, jusqu'à ce qu'on y mette fin."><input type="date" className="rh-champ-saisie" value={fin} onChange={(e) => setFin(e.target.value)} /></Champ>
      </div>
      <ChampMotif valeur={motif} onChange={setMotif} requis={false} libelleChamp="Précision (facultative)" aide="Conservée dans l'historique ; ne mentionnez aucune donnée de santé." />
    </FenetreActe>
  );
}

/** Ligne « conflit d'intérêts » d'un dossier (prêt, aide) : visible pour un mandataire, jamais sur son propre dossier. */
export function LigneDeport({ organisationId, salarie, droits }) {
  const { employe } = useAuth();
  const [ouvert, setOuvert] = useState(false);
  const [info, setInfo] = useState('');
  if (!salarie?.id || !droits.mandats.length || String(salarie.id) === String(employe?.id)) return null;
  return (
    <section className="rh-carte" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <div style={{ fontSize: 14 }}>
        <strong style={{ fontWeight: 500 }}>Conflit d'intérêts</strong>
        <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{info || 'Proche de ce salarié ? Déclarez un déport : une autre personne traitera ses dossiers.'}</span>
      </div>
      <Bouton petit Icone={ShieldOff} onClick={() => { setInfo(''); setOuvert(true); }}>Me déporter</Bouton>
      {ouvert && <FenetreDeclarerDeport organisationId={organisationId} salarie={salarie} onFermer={() => setOuvert(false)} onFait={(m) => { setOuvert(false); setInfo(m); }} />}
    </section>
  );
}

function FenetreTerminerDeport({ deport, onFermer, onFait }) {
  const api = useMiseEnPlace();
  const [fin, setFin] = useState(aujourdhuiDakar());
  return (
    <FenetreActe titre="Mettre fin au déport" Icone={UserX} libelleBouton="Mettre fin" pret={!!fin} onFermer={onFermer}
      aide="À partir du lendemain de cette date, la personne peut de nouveau agir sur les dossiers de ce salarié. Le déport reste dans l'historique."
      onConfirmer={async () => { const r = await api.terminerDeport(deport.id, fin); onFait('Fin du déport enregistrée.', { ...deport, date_fin: r?.date_fin || fin }); }}>
      <Champ libelle="Date de fin" requis><input type="date" className="rh-champ-saisie" value={fin} onChange={(e) => setFin(e.target.value)} /></Champ>
    </FenetreActe>
  );
}

/** Carte « Déports » de l'onglet Instances et mandats : liste, historique, déclaration, fin. */
export function CarteDeports({ organisation, droits }) {
  const api = useMiseEnPlace();
  const { employe } = useAuth();
  const administre = droits.a('ds_mandats_administrer');
  const mandataire = droits.mandats.length > 0;
  const [page, setPage] = useState(1);
  const liste = useLectureSociale(() => api.deports(organisation.id, { page, taille: 25 }), [api, organisation.id, page]);
  const [session, setSession] = useState([]);
  const [fenetre, setFenetre] = useState(null);
  const [info, setInfo] = useState('');
  if (!administre && !mandataire) return null;
  const indisponible = liste.erreur && liste.erreur.code === 'NON_DISPONIBLE';
  const serveur = toArray(liste.donnees);
  const lignes = [...serveur, ...session.filter((s) => !serveur.some((x) => x.id === s.id))];
  const peutTerminer = (d) => !d.date_fin && (administre || String(d.personne_id) === String(employe?.id));
  const retenir = (d) => setSession((l) => [d, ...l.filter((x) => x.id !== d.id)]);
  return (
    <section className="rh-carte">
      <TitreSection action={<Bouton petit Icone={ShieldOff} onClick={() => { setInfo(''); setFenetre({ type: 'declarer' }); }}>Déclarer un déport</Bouton>}>Déports (conflits d'intérêts)</TitreSection>
      <p className="rh-form-aide" style={{ margin: '0 0 12px' }}>
        Un membre se déporte pour un proche : il n'accomplit plus aucun acte de décision ni acte financier sur les dossiers de ce salarié. {administre ? 'Vous pouvez déclarer pour un autre membre.' : 'Vous déclarez votre propre déport.'}
      </p>
      {info && <BandeauInfo ton="succes">{info}</BandeauInfo>}
      {indisponible && <BandeauInfo ton="info" titre="Liste bientôt disponible">Le serveur ne propose pas encore la liste des déports : seuls ceux déclarés pendant cette session s'affichent ici. Chaque déport reste enregistré et appliqué.</BandeauInfo>}
      {liste.erreur && !indisponible && <BandeauInfo ton="alerte">{liste.erreur.message}</BandeauInfo>}
      {!lignes.length && (liste.donnees || indisponible) && <p className="rh-cellule-secondaire" style={{ margin: 0 }}>Aucun déport déclaré.</p>}
      {lignes.length > 0 && (
        <div className="rh-conteneur-tableau">
          <table className="rh-tableau">
            <thead><tr><th>Membre déporté</th><th>Salarié concerné</th><th>Période</th><th>État</th><th aria-label="Actions" /></tr></thead>
            <tbody>
              {lignes.map((d) => (
                <tr key={d.id}>
                  <td>{d.personne || d.personne_nom || '—'}{d.declare_par_nom && <span className="rh-cellule-secondaire" style={{ display: 'block' }}>déclaré par {d.declare_par_nom}</span>}</td>
                  <td>{d.salarie || d.salarie_nom || '—'}<span className="rh-cellule-secondaire" style={{ display: 'block' }}>{d.lien}</span></td>
                  <td>du {dateLongue(String(d.date_debut || '').slice(0, 10))}{d.date_fin ? ` au ${dateLongue(String(d.date_fin).slice(0, 10))}` : ''}</td>
                  <td>{d.date_fin && String(d.date_fin).slice(0, 10) < aujourdhuiDakar() ? <Badge ton="neutre" point>Terminé</Badge> : <Badge ton="alerte" point>En vigueur</Badge>}</td>
                  <td style={{ textAlign: 'right' }}>{peutTerminer(d) && <Bouton petit variante="discret" onClick={() => { setInfo(''); setFenetre({ type: 'terminer', d }); }}>Mettre fin</Bouton>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!indisponible && <PaginationDs donnees={liste.donnees} onPage={setPage} />}
      {fenetre?.type === 'declarer' && (
        <FenetreDeclarerDeport organisationId={organisation.id} administre={administre} onFermer={() => setFenetre(null)}
          onFait={(m, d) => { setFenetre(null); setInfo(m); retenir(d); liste.recharger(); }} />
      )}
      {fenetre?.type === 'terminer' && (
        <FenetreTerminerDeport deport={fenetre.d} onFermer={() => setFenetre(null)} onFait={(m, d) => { setFenetre(null); setInfo(m); retenir(d); liste.recharger(); }} />
      )}
    </section>
  );
}
